import { Box, keyframes } from "@mui/material";

// Text filled with the brand gradient that slowly sweeps across it - drawn so
// the sweep runs on the compositor.
//
// The obvious way, `background-clip: text` with an animated background-position,
// cannot be composited: Chrome repaints the text on every frame for as long as it
// is visible. Here the gradient is a layer twice the text's width, slid with a
// transform, and the text sits on top of it as black glyphs on white with
// `mix-blend-mode: screen`. Screen leaves the gradient untouched under black and
// turns everything under white to white, which is exactly "gradient clipped to
// the glyphs" - antialiased edges included - on a white ground.
//
// So it is only for text on an opaque WHITE background (TechSkills). Anywhere
// else the white box around the glyphs would show.
//
// The keyframes reproduce the old animation exactly: a 200%-wide background moving
// from position 0% to 100% is the same picture as a 200%-wide layer moving by
// half its own width.

const sweep = keyframes`
  0% { transform: translateX(0); }
  50% { transform: translateX(-50%); }
  100% { transform: translateX(0); }
`;

const GRADIENT = "linear-gradient(-45deg, #ee7752, #e73c7e, #23a6d5, #23d5ab)";

const GradientText = ({ component = "span", inline = false, children, sx }) => (
  <Box
    component={component}
    sx={{
      position: "relative",
      display: inline ? "inline-block" : "block",
      isolation: "isolate",
      // Clips the double-width layer without `overflow: hidden`, which would
      // move an inline-block's baseline off the surrounding text.
      contain: "paint",
      ...sx,
    }}
  >
    <Box
      component="span"
      aria-hidden="true"
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "200%",
        height: "100%",
        background: GRADIENT,
        animation: `${sweep} 7s ease infinite`,
      }}
    />
    <Box
      component="span"
      sx={{
        position: "relative",
        display: "block",
        color: "#000",
        backgroundColor: "#fff",
        mixBlendMode: "screen",
      }}
    >
      {children}
    </Box>
  </Box>
);

export default GradientText;
